import { useState, useEffect } from 'react'
import { pagesAPI } from '../../services/api'
import { LexicalEditor } from '@werkstatt/lexical-editor'

import { logger } from '@/utils/logger'

export default function PagesManagement() {
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingPage, setEditingPage] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_published: false,
  })

  useEffect(() => {
    loadPages()
  }, [])

  const loadPages = async () => {
    try {
      const response = await pagesAPI.getAllAdmin()
      setPages(response.data)
    } catch (err) {
      setError('Fehler beim Laden der Seiten')
      logger.error('Error loading pages:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (page = null) => {
    if (page) {
      setEditingPage(page)
      setFormData({
        title: page.title,
        slug: page.slug,
        content: page.content || '',
        meta_description: page.meta_description || '',
        is_published: page.is_published,
      })
    } else {
      setEditingPage(null)
      setFormData({
        title: '',
        slug: '',
        content: '',
        meta_description: '',
        is_published: false,
      })
    }
    setShowModal(true)
    setError('')
    setSuccess('')
  }

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[äöü]/g, match => ({ ä: 'ae', ö: 'oe', ü: 'ue' }[match]))
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

    setFormData({ ...formData, slug })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingPage) {
        await pagesAPI.update(editingPage.id, formData)
        setSuccess('Seite erfolgreich aktualisiert')
      } else {
        await pagesAPI.create(formData)
        setSuccess('Seite erfolgreich erstellt')
      }

      await loadPages()
      setShowModal(false)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern der Seite')
      logger.error('Error saving page:', err)
    }
  }

  const handleDelete = async (page) => {
    if (!confirm(`Möchten Sie die Seite "${page.title}" wirklich löschen?`)) {
      return
    }

    try {
      await pagesAPI.delete(page.id)
      setSuccess('Seite erfolgreich gelöscht')
      await loadPages()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen der Seite')
      setTimeout(() => setError(''), 3000)
    }
  }

  const handleClone = async (page) => {
    const newTitle = prompt('Titel für die Kopie:', `${page.title} (Kopie)`)
    if (!newTitle) return

    const newSlug = prompt('URL-Slug für die Kopie:', `${page.slug}-kopie`)
    if (!newSlug) return

    try {
      await pagesAPI.clone(page.id, newTitle, newSlug)
      setSuccess('Seite erfolgreich kopiert')
      await loadPages()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Fehler beim Kopieren der Seite')
      setTimeout(() => setError(''), 3000)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
          + Neue Seite
        </button>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      {pages.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ alignItems: 'center', textAlign: 'center', padding: '48px 24px' }}>
          <h3 className="wc-card-title" style={{ margin: 0 }}>Keine Seiten vorhanden</h3>
          <p className="wc-muted" style={{ fontSize: 14, margin: '4px 0 12px' }}>Erstellen Sie Ihre erste CMS-Seite</p>
          <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
            Erste Seite erstellen
          </button>
        </div>
      ) : (
        <div className="wc-card wc-elev-sm" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Zuletzt geändert</th>
                  <th style={{ textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((page) => (
                  <tr key={page.id}>
                    <td>{page.title}</td>
                    <td className="wc-muted">/{page.slug}</td>
                    <td>
                      <span className={`wc-tag ${page.is_published ? 'wc-tag-accent' : 'wc-tag-neutral'}`}>
                        {page.is_published ? 'Veröffentlicht' : 'Entwurf'}
                      </span>
                    </td>
                    <td className="wc-muted">
                      {page.updated_at
                        ? new Date(page.updated_at).toLocaleDateString('de-DE')
                        : new Date(page.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleOpenModal(page)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                        Bearbeiten
                      </button>
                      <button onClick={() => handleClone(page)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                        Klonen
                      </button>
                      <button
                        onClick={() => handleDelete(page)}
                        className="wc-btn wc-btn-ghost"
                        style={{ padding: '4px 8px', color: '#fca5a5' }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="wc-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="wc-modal wc-modal-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>
              {editingPage ? 'Seite bearbeiten' : 'Neue Seite'}
            </h3>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="wc-input"
                    required
                  />
                </div>

                <div className="wc-field">
                  <label>URL-Slug *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="wc-input"
                      placeholder="z.B. impressum"
                      required
                      style={{ flex: 1 }}
                    />
                    <button type="button" onClick={generateSlug} className="wc-btn wc-btn-secondary" style={{ whiteSpace: 'nowrap' }}>
                      Auto
                    </button>
                  </div>
                  <p className="wc-muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
                    Seite erreichbar unter: /{formData.slug}
                  </p>
                </div>
              </div>

              <div className="wc-field">
                <label>Meta-Beschreibung (SEO)</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  rows={2}
                  className="wc-input"
                  placeholder="Kurze Beschreibung für Suchmaschinen"
                />
              </div>

              <div className="wc-field">
                <label>Inhalt (Rich-Text-Editor) *</label>
                <div style={{ background: 'var(--wc-neutral-100)', borderRadius: 'var(--wc-radius-md)', padding: 4, color: '#1a1a1a' }}>
                  <LexicalEditor
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Seiteninhalt hier eingeben..."
                    accentColor="green"
                  />
                </div>
              </div>

              <label className="wc-check">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                />
                Seite veröffentlichen
              </label>

              {error && <div className="wc-alert wc-alert-error">{error}</div>}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setShowModal(false)} className="wc-btn wc-btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="wc-btn wc-btn-primary">
                  {editingPage ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
